import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Search, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const norm = (c) => String(c || "").replace(/\s+/g, "").toLowerCase();
const LEVELS = {
  professional: { label: "Profesionál", title: "Platný", text: "Tento profesionál je oficiálne zapísaný vo verejnom registri SauberEko® a jeho certifikácia je platná." },
  beginner: { label: "Začiatočník", title: "Začiatočník", text: "Tento uchádzač je na začiatku svojej odbornej cesty v SauberEko®. Základné znalosti." },
  graduate: { label: "Absolvent programu", title: "Absolvent programu", text: "Absolvent prešiel programom Pracuj, uč sa a zarábaj (Work, learn and earn©). Pokročilé znalosti." },
};

function BadgeRow({ p, badge, token, onSaved }) {
  const [level, setLevel] = useState(badge?.level || "professional");
  const [title, setTitle] = useState(badge?.title || "");
  const [text, setText] = useState(badge?.text || "");
  const [busy, setBusy] = useState(false);
  const id = norm(p.certificate_no);
  const pick = (l) => { setLevel(l); setTitle(l === "professional" ? "" : LEVELS[l].title); setText(l === "professional" ? "" : LEVELS[l].text); };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/hof-badge`, { certificate_no: p.certificate_no, level, title, text }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved(data.badges || {}); toast.success(`Popis pri ${p.name} uložený.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Uloženie zlyhalo."); }
    finally { setBusy(false); }
  };
  return (
    <div className="py-4 grid gap-3 md:grid-cols-[220px_1fr]" data-testid={`hofbadge-row-${id}`}>
      <div className="min-w-0">
        <p className="text-white truncate">{p.name}</p>
        <p className="text-[#D4AF37]/80 text-xs font-mono">{p.certificate_no}</p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {Object.entries(LEVELS).map(([k, v]) => (
            <button key={k} type="button" onClick={() => pick(k)} data-testid={`hofbadge-level-${k}-${id}`}
              className={`text-[11px] px-2 py-1 border transition-colors ${level === k ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60 hover:border-[#D4AF37]/60"}`}>{v.label}</button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`Nadpis (predvolene: ${LEVELS.professional.title})`} data-testid={`hofbadge-title-${id}`}
          className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none" />
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Popis (predvolene: oficiálne zapísaný profesionál…)" data-testid={`hofbadge-text-${id}`}
          className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none" />
        <button type="button" onClick={save} disabled={busy} data-testid={`hofbadge-save-${id}`}
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-black text-sm font-semibold px-4 py-1.5 hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">
          {busy && <Loader2 size={14} className="animate-spin" />} Uložiť
        </button>
      </div>
    </div>
  );
}

export default function AdminHofBadges({ token }) {
  const [people, setPeople] = useState(null);
  const [badges, setBadges] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/hof/list`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data: d }) => setPeople([...(d.recruiters || []), ...(d.professionals || [])])).catch(() => setPeople([]));
    axios.get(`${API}/hof/badges`).then(({ data }) => setBadges(data.badges || {})).catch(() => setBadges({}));
  }, []);

  const list = (people || []).filter((p) => !q.trim() || `${p.name} ${p.certificate_no}`.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div data-testid="admin-hof-badges" className="mt-12 border border-[#D4AF37]/30 bg-gradient-to-b from-[#0d0d0c] to-[#080808] p-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between mb-5">
        <div className="flex items-center gap-3">
          <BadgeCheck size={28} className="text-[#D4AF37]" />
          <div>
            <h3 className="font-display text-xl text-white">Úroveň a popis v Sieni slávy</h3>
            <p className="text-white/45 text-sm">Zobrazí sa v zlatom rámčeku pri mene. Prázdne = štandardný „Platný“.</p>
          </div>
        </div>
        <div className="relative sm:w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hľadať meno…" data-testid="hofbadge-search"
            className="w-full bg-black border border-white/15 pl-8 pr-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none" />
        </div>
      </div>
      {people === null || badges === null ? (
        <p className="text-white/40 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Načítavam…</p>
      ) : (
        <div className="divide-y divide-white/5">
          {list.map((p) => <BadgeRow key={p.certificate_no} p={p} badge={badges[norm(p.certificate_no)]} token={token} onSaved={setBadges} />)}
          {list.length === 0 && <p className="text-white/40 text-sm py-3">Nič sa nenašlo.</p>}
        </div>
      )}
    </div>
  );
}
